import type { FeedItem } from '@tunasong/models'
import { Typography, VBox, useScroll } from '@tunasong/ui-lib'
import mergeRefs from 'merge-refs'
import { useEffect, useState } from 'react'
import type { FC, HTMLAttributes, ReactNode } from 'react'
import cn from 'classnames'
import ChatFeedItem from './chat-feed-item.js'
import styles from './chat-feed.module.css'

/** Content that sits in the feed on its own, such as an anchored group in the sidebar Chat (chat makeover M3) */
export interface ChatFeedStandalone {
  id: string
  standalone: true
  createdAt: string
  content: ReactNode
}

export type ChatFeedEntry = FeedItem | ChatFeedStandalone

const isStandalone = (entry: ChatFeedEntry): entry is ChatFeedStandalone => 'standalone' in entry && entry.standalone

export interface ChatFeedProps extends HTMLAttributes<HTMLDivElement> {
  className?: string
  title?: string
  items: ChatFeedEntry[]
  maxItems?: number
  /** The first/top element. Set if you stable scroll behavior for dynamic lists */
  topRef?: React.RefObject<HTMLDivElement> | React.RefCallback<HTMLDivElement>
}

export const ChatFeed: FC<ChatFeedProps> = props => {
  const { className, topRef, title, maxItems = Number.MAX_SAFE_INTEGER, items = [], ...restProps } = props
  const showItems = items.slice(0, maxItems)

  const { highlightId } = useScroll(items.filter((item): item is FeedItem => !isStandalone(item)))
  const [lastRef, setLastRef] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!lastRef) {
      return
    }
    /** Since the editors take some time to render, we hack this */
    setTimeout(() => {
      lastRef.scrollIntoView({ block: 'end' })
    }, 100)
  }, [lastRef])

  return (
    <VBox {...restProps} className={cn(styles.container, className)}>
      {title ? (
        <Typography className={styles.title} variant="subtitle1">
          {title}
        </Typography>
      ) : null}

      {showItems.map((item, idx) => {
        const ref = mergeRefs<HTMLDivElement>(
          idx === 0 ? topRef : undefined,
          idx === showItems.length - 1 ? setLastRef : undefined
        )
        return isStandalone(item) ? (
          <div key={item.id} ref={ref} className={styles.standalone}>
            {item.content}
          </div>
        ) : (
          <ChatFeedItem key={item.id} ref={ref} item={item} highlight={highlightId === item.id} />
        )
      })}
    </VBox>
  )
}

export default ChatFeed
